import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Moon } from 'lucide-react';
import { useIslamojis } from '../hooks/useIslamojis';

const CATEGORIES = ['people', 'food', 'things', 'places', 'sayings'];
const CATEGORY_LABELS = { people: 'People', food: 'Food', things: 'Things', places: 'Places', sayings: 'Sayings' };

const IslamojiPicker = ({ onSelectEmoji, onSelectGif, showGifs = true }) => {
  const islamojis = useIslamojis();
  const [activeTab, setActiveTab] = useState('people');

  const byCategory = useMemo(() => {
    const map = {};
    CATEGORIES.forEach(c => { map[c] = []; });
    const gifs = [];
    islamojis.forEach(item => {
      if (item.type === 'gif') gifs.push(item);
      else if (map[item.category]) map[item.category].push(item);
    });
    return { ...map, gifs };
  }, [islamojis]);

  const activeItems = activeTab === 'gifs' ? byCategory.gifs : (byCategory[activeTab] || []);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="mt-3 p-3 rounded-arch-lg"
      style={{ background: 'rgba(74,173,168,0.06)', border: '1px solid rgba(74,173,168,0.15)' }}
      data-testid="islamoji-picker"
    >
      {/* Category tabs — each icon is a real Islamoji from that category */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {CATEGORIES.map(cat => {
          const preview = byCategory[cat]?.[0];
          const isActive = activeTab === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveTab(cat)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isActive ? 'bg-primary text-primary-foreground' : 'bg-white/60 text-text-muted hover:bg-primary/10 hover:text-primary'
              }`}
              data-testid={`islamoji-tab-${cat}`}
            >
              {preview ? (
                <img src={preview.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
              ) : (
                <Moon className="w-3.5 h-3.5" aria-hidden="true" />
              )}
              {CATEGORY_LABELS[cat]}
            </button>
          );
        })}
        {showGifs && (
          <button
            type="button"
            onClick={() => setActiveTab('gifs')}
            className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
              activeTab === 'gifs' ? 'bg-primary text-primary-foreground' : 'bg-white/60 text-text-muted hover:bg-primary/10 hover:text-primary'
            }`}
            data-testid="islamoji-tab-gifs"
          >
            GIFs
          </button>
        )}
      </div>

      {/* Grid */}
      {activeItems.length === 0 ? (
        <p className="text-xs text-text-muted text-center py-4">No Islamojis here yet — check back soon!</p>
      ) : (
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
          {activeItems.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => (item.type === 'gif' ? onSelectGif(item) : onSelectEmoji(item))}
              className="aspect-square rounded-arch overflow-hidden border border-primary/15 hover:border-primary/50 hover:scale-105 transition-all bg-white/50"
              title={item.name}
              aria-label={item.name}
              data-testid={`islamoji-item-${item.id}`}
            >
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default IslamojiPicker;
